import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  login,
  getGoogleAuthorizeUrl,
  appleSsoLogin,
  googleSsoLogin,
  isSsoTenantSelectionRequired,
  isSsoLinkingRequired,
  completePendingSsoLink,
} from '../../api/authApi';
import { signInWithApple, isAppleSsoConfigured } from '@pp/shared/auth/appleAuth';
import { isNativeApp } from '@pp/shared/platform/platform';
import {
  enableBiometric,
  getBiometryLabel,
  isBiometricAvailable,
  isBiometricEnabled,
  markEnrollmentDeclined,
  wasEnrollmentDeclined,
  type BiometryLabel,
} from '@pp/shared/auth/biometricAuth';
import { Fingerprint, ScanFace } from 'lucide-react';
import {
  enablePush,
  pushPermissionGranted,
  pushSupported,
} from '@pp/shared/push/push';
import {
  signInWithAppleNative,
  signInWithGoogleNative,
  isGoogleSsoConfigured as isNativeGoogleConfigured,
  isNativeAppleConfigured,
} from '@pp/shared/auth/socialLogin';
import type { LoginResponse, User } from '../../types';
import { useAppStore } from '../../contexts/store';
import { setTenantSubdomain } from '@pp/shared/platform/tenant';
import Card from '../../components/common/Card';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import GoogleIcon from '../../components/common/GoogleIcon';
import MfaVerification from '../../components/auth/MfaVerification';
import { toast } from 'react-hot-toast';
import { sanitizeLogoUrl, sanitizeEmail } from '../../utils/validation';
import { useRateLimit } from '../../hooks/useRateLimit';
import { useSEO, SEO_CONFIGS } from '../../hooks/useSEO';
import { isMfaRequired, type MfaRequiredResponse } from '../../api/mfaApi';
import {
  setOAuthState,
  setOAuthRedirect,
  setOAuthOrigin,
  setOAuthReturnPath,
  setOAuthTenant,
  getOAuthOrigin,
  clearAllOAuthData,
} from '@pp/shared/auth/oauthStorage';
import { getTenantSubdomain } from '@pp/shared/platform/tenant';

const CENTRALIZED_CALLBACK_URL = 'https://app.packetprospector.com/auth/google/callback';

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export function LoginPage() {
  // Apply SEO meta tags
  useSEO(SEO_CONFIGS.login);

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { setUser, setToken, setRefreshToken, setBiometricUnlocked, tenantConfig } = useAppStore();

  // Get the path the user was trying to access before being redirected to login.
  // Use useRef to preserve the value across re-renders.
  //
  // Sources, in priority order:
  //  1. React Router state (`location.state.from.pathname`) — set when the
  //     redirect was a soft `navigate()` from an in-app guard like FieldLayout.
  //  2. `?from=...` query string — set by the axios 401 interceptor, which
  //     uses a hard `window.location.href` (state would be lost otherwise).
  //
  // Without (2), a session timeout from inside the field PWA always lands the
  // user on /dashboard after login — defeating the muscle memory of the field
  // tab they were on.
  const fromPathRef = useRef<string | undefined>(
    (location.state as { from?: { pathname: string } })?.from?.pathname
      ?? (() => {
        const raw = searchParams.get('from');
        if (!raw) return undefined;
        try {
          const decoded = decodeURIComponent(raw);
          // Defense-in-depth: only allow same-origin paths so a crafted link
          // can't bounce the user offsite after login.
          return decoded.startsWith('/') && !decoded.startsWith('//') ? decoded : undefined;
        } catch {
          return undefined;
        }
      })()
  );
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  // Native shells use the platform Google SDK via @capgo/capacitor-
  // social-login. Web uses the existing redirect-based Google OAuth and
  // appleid.auth.js JS SDK. The button is hidden when the corresponding
  // env-configured client ID is missing so dev environments don't show a
  // button that always errors.
  //
  // Apple SSO is required on native iOS: App Review rejected the app under
  // Guideline 4.8 when we shipped Google without it (they refused the
  // enterprise-exemption argument — see docs/apple-submission/metadata.md).
  // Accounts stay invite-only: an Apple ID with no provisioned account is
  // routed to the "link your work account" screen below instead of a dead
  // end, which is also how the App Review reviewer completes the flow with
  // the demo credentials.
  const isNative = isNativeApp();
  const googleEnabled = isNative ? isNativeGoogleConfigured() : true;
  const appleEnabled = isNative ? isNativeAppleConfigured() : isAppleSsoConfigured();

  // Track if we've already initiated auto-Google login to prevent loops
  const autoGoogleInitiated = useRef(false);

  // MFA state
  const [mfaData, setMfaData] = useState<MfaRequiredResponse | null>(null);

  // SSO multi-tenant selection: when a Google/Apple sign-in matches accounts
  // in several organizations, the backend returns the tenant list and we
  // re-submit the same (still-valid) identity token with the chosen
  // subdomain. Holding the token in state is safe — the verifiers validate
  // signature + nonce hash, not one-time use, and tokens expire in minutes.
  const [ssoSelection, setSsoSelection] = useState<{
    provider: 'google' | 'apple';
    token: string;
    nonce?: string;
    firstName?: string;
    lastName?: string;
    tenants: Array<{ subdomain: string; companyName: string }>;
  } | null>(null);
  const [ssoSelectLoading, setSsoSelectLoading] = useState(false);

  // SSO identity verified but no account is linked to it (accounts are
  // invite-only — think reviewer's personal Apple ID, or Hide My Email
  // relay addresses that can't match a provisioned work email). We hold the
  // server's short-lived pending-link token; handleLoginSuccess redeems it
  // silently after the user's next normal sign-in completes.
  //
  // App Review rejected build 32 (Guideline 4) for showing a PASSWORD FORM
  // as the immediate next step of Sign in with Apple. So the flow is now:
  // an informational guidance screen (no input fields) → the STANDARD login
  // form → silent link on success. Same mechanics, no password prompt
  // presented "inside" the Apple flow.
  const [pendingSsoLink, setPendingSsoLink] = useState<{
    provider: 'google' | 'apple';
    pendingLinkToken: string;
    email: string;
  } | null>(null);
  // True while the informational screen shows; false once the user chooses
  // "sign in with work email" and returns to the standard form (with
  // pendingSsoLink still set so the link completes silently after login).
  const [showLinkGuidance, setShowLinkGuidance] = useState(false);

  // Post-login biometric enrollment offer (native only) — promise-based so
  // handleLoginSuccess can await the user's choice before navigating
  const [enrollPrompt, setEnrollPrompt] = useState<{
    label: BiometryLabel;
    resolve: (enable: boolean) => void;
  } | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  // Client-side rate limit as a soft guard against runaway retries. The
  // server's account lockout is the real defense; keep this loose enough
  // that a typo-prone user on a shared device isn't locked out client-side
  // before the server ever weighs in.
  const { checkRateLimit, isBlocked, remainingTime } = useRateLimit(10, 60000);

  // Sanitize logo URL to prevent SSRF and XSS
  const safeLogoUrl = tenantConfig?.logoUrl
    ? sanitizeLogoUrl(tenantConfig.logoUrl)
    : null;

  // Initiate Google OAuth - extracted for use by both button click and auto-start
  const initiateGoogleLogin = useCallback(async () => {
    setGoogleLoading(true);
    try {
      // CRITICAL: Unregister service workers BEFORE redirecting to Google
      // This prevents the SW from intercepting the callback and returning cached content
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        if (registrations.length > 0) {
          await Promise.all(registrations.map(r => r.unregister()));
        }
      }

      // Clear any stale OAuth data from previous attempts
      // This prevents state mismatch errors in non-incognito browsers
      clearAllOAuthData();

      // Use centralized callback URL (app.packetprospector.com) for all tenants
      const redirectUri = CENTRALIZED_CALLBACK_URL;

      // Get the Google authorization URL from the backend
      const response = await getGoogleAuthorizeUrl(redirectUri, tenantConfig?.tenantId);

      // Store state and redirect URI in cookies (works across subdomains)
      setOAuthState(response.state);
      setOAuthRedirect(redirectUri);

      // Store the originating hostname if not already set (for cross-origin redirects)
      if (!getOAuthOrigin()) {
        setOAuthOrigin(window.location.origin);
      }

      // Store the tenant subdomain for cross-subdomain OAuth flow
      // This ensures the correct tenant context when callback happens on app.packetprospector.com
      const currentTenant = getTenantSubdomain();
      if (currentTenant) {
        setOAuthTenant(currentTenant);
      }

      // Store the return path if user was redirected from a protected route (e.g., /field)
      if (fromPathRef.current) {
        setOAuthReturnPath(fromPathRef.current);
      }

      // Redirect to Google OAuth
      window.location.href = response.authorizationUrl;
    } catch (error) {
      console.error('Failed to initiate Google login:', error);
      toast.error('Google sign-in is not available at this time.');
      setGoogleLoading(false);
    }
  }, [tenantConfig?.tenantId]);

  // Handle auto-start Google login when redirected from marketing site or tenant subdomain
  // URL params: ?google=true&returnTo=https://packetprospector.com
  useEffect(() => {
    const shouldAutoGoogle = searchParams.get('google') === 'true';
    const returnTo = searchParams.get('returnTo');

    if (shouldAutoGoogle && !autoGoogleInitiated.current) {
      autoGoogleInitiated.current = true;

      // Clean up URL params
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('google');
      newUrl.searchParams.delete('returnTo');
      window.history.replaceState({}, '', newUrl.toString());

      // Store the return URL as the origin (if provided)
      if (returnTo) {
        try {
          // Validate it's a valid URL and from our domain
          const returnUrl = new URL(decodeURIComponent(returnTo));
          if (returnUrl.hostname.endsWith('packetprospector.com')) {
            setOAuthOrigin(returnUrl.origin);
          }
        } catch {
          // Invalid URL, ignore
        }
      }

      // Start Google OAuth flow (defer to next tick to ensure handleGoogleLogin is defined)
      setTimeout(() => {
        initiateGoogleLogin();
      }, 0);
    }
  }, [searchParams, initiateGoogleLogin]);

  const handleLoginSuccess = async (response: LoginResponse) => {
    setToken(response.accessToken);
    setRefreshToken(response.refreshToken);
    setUser(response.user as User);

    // Store tenant subdomain for API requests
    if (response.tenantSubdomain) {
      setTenantSubdomain(response.tenantSubdomain);
    }

    // Mark this session as biometric-unlocked so the gate doesn't bounce the
    // user to /unlock immediately after they just typed their password.
    setBiometricUnlocked(true);

    // The user reached this password sign-in from a Google/Apple attempt
    // that found no linked account. Now that they've proven ownership,
    // attach the verified external identity so the NEXT social sign-in
    // matches directly. Failure is non-fatal — they're already signed in.
    if (pendingSsoLink) {
      const providerLabel = pendingSsoLink.provider === 'apple' ? 'Apple ID' : 'Google account';
      try {
        await completePendingSsoLink(pendingSsoLink.pendingLinkToken);
        toast.success(`Your ${providerLabel} is now linked — use it to sign in next time.`);
      } catch {
        toast(`Couldn't link your ${providerLabel} this time. You can still sign in normally.`);
      }
      setPendingSsoLink(null);
    }

    toast.success('Login successful!');

    // On native, if biometric is available but the user hasn't enrolled yet,
    // ask if they want to enable it. We do this BEFORE navigating so the
    // OS-level Face ID sheet doesn't fight the page transition.
    await maybeOfferBiometricEnrollment();

    // Refresh push installation in the background. No-op on web and
    // when the user hasn't yet granted push permission — the
    // permission prompt belongs to a deliberate Settings opt-in, not
    // the login flow.
    void refreshPushRegistrationIfGranted();

    // Determine redirect path:
    // 1. If user was trying to access a specific path (e.g., /field), go there
    // 2. Native shell (Dispatch app) defaults to /field — the dashboard layout
    //    isn't wired into the native nav, so dropping the user on /dashboard
    //    after a password fallback (e.g. Face ID failed → "Use password
    //    instead") strands them with no way back into Dispatch.
    // 3. Otherwise, SuperAdmins go to admin panel, others to dashboard
    if (fromPathRef.current) {
      navigate(fromPathRef.current);
    } else if (isNative) {
      navigate('/field');
    } else if (response.user.role === 'SuperAdmin') {
      navigate('/superadmin');
    } else {
      navigate('/dashboard');
    }
  };

  // Silent push re-registration. If the user has already granted push
  // permission (returning user, fresh install of an app they had
  // before, or a Settings-screen opt-in earlier), refresh the
  // installation with the current token + new user binding. We do NOT
  // request permission here — iOS only lets the app ask once, so the
  // prompt belongs at a moment the user is explicitly opting in
  // (Settings toggle, or a deferred banner after first job assignment).
  const refreshPushRegistrationIfGranted = async () => {
    if (!pushSupported()) return;
    if (!(await pushPermissionGranted())) return;
    try {
      await enablePush();
    } catch {
      // Token refresh failures are non-fatal; the next launch retries.
    }
  };

  const maybeOfferBiometricEnrollment = async () => {
    // Skip when not native, already enrolled, or the user said "Not now"
    // before — the Settings toggle remains the way to enable it later.
    if (!isNativeApp() || isBiometricEnabled() || wasEnrollmentDeclined()) return;
    if (!(await isBiometricAvailable())) return;

    const label = await getBiometryLabel();
    // Branded in-app dialog instead of window.confirm's bare OS alert. The
    // promise resolves when the user picks; handleLoginSuccess awaits it so
    // navigation happens after the choice.
    const wantsEnable = await new Promise<boolean>((resolve) => {
      setEnrollPrompt({ label, resolve });
    });
    setEnrollPrompt(null);

    if (!wantsEnable) {
      markEnrollmentDeclined();
      return;
    }

    try {
      await enableBiometric();
      toast.success(`${label} enabled. You'll use it next time you open the app.`);
    } catch {
      // User cancelled the enrollment biometric prompt — silent, and not
      // treated as a "Not now" (they said yes; the OS sheet just failed).
    }
  };

  const onSubmit = async (data: LoginFormData) => {
    // Check rate limit before attempting login
    if (!checkRateLimit()) {
      toast.error(`Too many login attempts. Please wait ${remainingTime} seconds.`);
      return;
    }

    setLoading(true);
    try {
      const response = await login({
        email: sanitizeEmail(data.email),  // Sanitize email
        password: data.password,  // Don't sanitize passwords
      });

      // Check if MFA is required
      if (isMfaRequired(response)) {
        setMfaData(response);
        return;
      }

      handleLoginSuccess(response);
    } catch (error) {
      const errorMessage = error instanceof Error && 'response' in error
        ? (error.response as { data?: { message?: string } })?.data?.message
        : undefined;
      toast.error(errorMessage || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSuccess = (response: unknown) => {
    handleLoginSuccess(response as LoginResponse);
  };

  const handleMfaCancel = () => {
    setMfaData(null);
  };

  // Route an SSO "no linked account" response to the guidance screen.
  // Prefill the email field only when the provider shared a real address —
  // a Hide My Email relay is never the work email the user signs in with.
  const startPendingSsoLink = (
    provider: 'google' | 'apple',
    response: { pendingLinkToken: string; email: string },
  ) => {
    setPendingSsoLink({
      provider,
      pendingLinkToken: response.pendingLinkToken,
      email: response.email,
    });
    setShowLinkGuidance(true);
    if (response.email && !response.email.endsWith('@privaterelay.appleid.com')) {
      setValue('email', response.email);
    }
  };

  const handleGoogleLogin = async () => {
    // Native: use the platform Google SDK + the unified /auth/google-sso
    // endpoint. Web: keep the existing redirect-based OAuth flow.
    if (isNative) {
      if (!googleEnabled) return;
      setGoogleLoading(true);
      try {
        const idToken = await signInWithGoogleNative();
        const response = await googleSsoLogin(
          idToken,
          tenantConfig?.subdomain ?? undefined,
        );
        if (isSsoTenantSelectionRequired(response)) {
          setSsoSelection({
            provider: 'google',
            token: idToken,
            tenants: response.tenants,
          });
          return;
        }
        if (isSsoLinkingRequired(response)) {
          startPendingSsoLink('google', response);
          return;
        }
        handleLoginSuccess(response);
      } catch (err) {
        // Prefer the server's message (e.g. "We couldn't find a
        // PacketProspector account for that Google account. Ask your ISP's
        // admin to invite you, then try again.") over axios's generic
        // "Request failed with status code 401" — same extraction the
        // password onSubmit uses above.
        const serverMessage =
          err instanceof Error && 'response' in err
            ? (err.response as { data?: { message?: string } })?.data?.message
            : undefined;
        const message = serverMessage ?? (err instanceof Error ? err.message : 'Sign in with Google failed');
        if (message.toLowerCase().includes('cancel')) return;
        console.error('Native Google sign-in failed:', err);
        toast.error(message);
      } finally {
        setGoogleLoading(false);
      }
      return;
    }

    // Store current origin for redirect after auth (button click always uses current origin)
    setOAuthOrigin(window.location.origin);
    initiateGoogleLogin();
  };

  const handleAppleLogin = async () => {
    if (!appleEnabled) return;
    setAppleLoading(true);
    try {
      // Pull an identity token from the native SDK (iOS) or the web JS SDK.
      // Both surfaces target the same /auth/apple-sso endpoint.
      const apple = isNative
        ? await signInWithAppleNative()
        : await signInWithApple();

      const response = await appleSsoLogin(apple.identityToken, {
        nonce: apple.nonce,
        firstName: apple.firstName,
        lastName: apple.lastName,
        tenantSubdomain: tenantConfig?.subdomain ?? undefined,
      });

      if (isSsoTenantSelectionRequired(response)) {
        setSsoSelection({
          provider: 'apple',
          token: apple.identityToken,
          nonce: apple.nonce,
          firstName: apple.firstName,
          lastName: apple.lastName,
          tenants: response.tenants,
        });
        return;
      }

      if (isSsoLinkingRequired(response)) {
        // This is the exact case that used to dead-end App Review (their
        // Apple ID has no provisioned account): now it routes to a designed
        // linking screen instead of a 401 toast.
        startPendingSsoLink('apple', response);
        return;
      }

      handleLoginSuccess(response);
    } catch (err) {
      // Prefer the server's message (e.g. "We couldn't find a
      // PacketProspector account for that Apple ID. Ask your ISP's admin
      // to invite you, then try again.") over axios's generic
      // "Request failed with status code 401". This is the exact case
      // that caused the App Store reviewer's session to spin: the
      // reviewer's personal Apple ID has no PP account, the backend
      // returns 401, and without this extraction the user sees no
      // explanation of why.
      const serverMessage =
        err instanceof Error && 'response' in err
          ? (err.response as { data?: { message?: string } })?.data?.message
          : undefined;
      const message = serverMessage ?? (err instanceof Error ? err.message : 'Sign in with Apple failed');
      // User-cancelled the Apple sheet — silent on both web (popup_closed)
      // and native (cancel / cancelled / errCanceled).
      if (message.toLowerCase().includes('popup_closed') || message.toLowerCase().includes('cancel')) {
        return;
      }
      console.error('Apple sign-in failed:', err);
      toast.error(message);
    } finally {
      setAppleLoading(false);
    }
  };

  const handleSsoTenantChoice = async (subdomain: string) => {
    if (!ssoSelection || ssoSelectLoading) return;
    setSsoSelectLoading(true);
    try {
      const response =
        ssoSelection.provider === 'google'
          ? await googleSsoLogin(ssoSelection.token, subdomain)
          : await appleSsoLogin(ssoSelection.token, {
              nonce: ssoSelection.nonce,
              firstName: ssoSelection.firstName,
              lastName: ssoSelection.lastName,
              tenantSubdomain: subdomain,
            });
      if (isSsoTenantSelectionRequired(response)) {
        // Shouldn't happen once a subdomain is supplied — treat as failure.
        toast.error('Could not sign in to that organization. Please try again.');
        return;
      }
      if (isSsoLinkingRequired(response)) {
        // Also shouldn't happen (selection implies matching users exist),
        // but route it properly rather than crashing on a missing token.
        setSsoSelection(null);
        startPendingSsoLink(ssoSelection.provider, response);
        return;
      }
      setSsoSelection(null);
      handleLoginSuccess(response);
    } catch (err) {
      // The identity token expires minutes after the provider issued it; if
      // the user lingered on this screen the re-submit can 401. Send them
      // back to the provider button for a fresh token.
      const serverMessage =
        err instanceof Error && 'response' in err
          ? (err.response as { data?: { message?: string } })?.data?.message
          : undefined;
      console.error('SSO tenant selection sign-in failed:', err);
      toast.error(serverMessage || 'Sign-in expired. Please try again.');
      setSsoSelection(null);
    } finally {
      setSsoSelectLoading(false);
    }
  };

  // Post-login biometric enrollment offer (native). Rendered as a
  // full-screen branded card; the pending login navigation continues once
  // the promise resolves.
  if (enrollPrompt) {
    const EnrollIcon = enrollPrompt.label === 'Face ID' ? ScanFace : Fingerprint;
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-secondary/5 px-4 py-12">
        <Card className="w-full max-w-sm shadow-xl" variant="elevated">
          <div className="flex flex-col items-center text-center gap-6 p-2">
            <div className="rounded-full bg-primary/10 p-6">
              <EnrollIcon className="w-12 h-12 text-primary" aria-hidden="true" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-text">
                Faster sign-in with {enrollPrompt.label}?
              </h1>
              <p className="text-sm text-text-secondary mt-2">
                Skip typing your password next time. You can always sign in with
                your password instead, or turn this on later in your Profile.
              </p>
            </div>
            <div className="flex flex-col w-full gap-2">
              <Button className="w-full" onClick={() => enrollPrompt.resolve(true)}>
                Enable {enrollPrompt.label}
              </Button>
              <Button
                variant="ghost"
                className="w-full"
                onClick={() => enrollPrompt.resolve(false)}
              >
                Not now
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // SSO matched multiple organizations — let the user pick one
  if (ssoSelection) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-secondary/5 px-4 py-12">
        <Card className="w-full max-w-md shadow-xl" variant="elevated">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-text mb-2">Choose your organization</h1>
            <p className="text-text-secondary text-sm">
              That {ssoSelection.provider === 'google' ? 'Google account' : 'Apple ID'} is a
              member of more than one organization.
            </p>
          </div>
          <div className="space-y-3">
            {ssoSelection.tenants.map((tenant) => (
              <button
                key={tenant.subdomain}
                type="button"
                onClick={() => handleSsoTenantChoice(tenant.subdomain)}
                disabled={ssoSelectLoading}
                className="w-full p-4 text-left bg-surface-secondary hover:bg-surface-tertiary rounded-lg transition-colors border border-border disabled:opacity-50"
              >
                <div className="font-medium text-text">{tenant.companyName}</div>
                <div className="text-sm text-text-secondary">
                  {tenant.subdomain}.packetprospector.com
                </div>
              </button>
            ))}
          </div>
          <Button
            type="button"
            variant="ghost"
            className="w-full mt-4"
            onClick={() => setSsoSelection(null)}
            disabled={ssoSelectLoading}
          >
            Back to sign in
          </Button>
        </Card>
      </div>
    );
  }

  // If MFA is required, show verification component
  if (mfaData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-secondary/5 px-4 py-12">
        <Card className="w-full max-w-md shadow-xl" variant="elevated">
          {safeLogoUrl && (
            <div className="mb-6 text-center">
              <img
                src={safeLogoUrl}
                alt={tenantConfig?.companyName || 'Logo'}
                className="h-16 w-auto mx-auto"
                loading="lazy"
                referrerPolicy="no-referrer"
              />
            </div>
          )}
          <MfaVerification
            mfaSessionToken={mfaData.mfaSessionToken}
            email={mfaData.email}
            expiresAt={mfaData.expiresAt}
            onSuccess={handleMfaSuccess}
            onCancel={handleMfaCancel}
          />
        </Card>
      </div>
    );
  }

  // SSO identity verified but not linked to any account — informational
  // guidance, then back to the standard sign-in form. handleLoginSuccess
  // redeems the pending-link token silently after that normal sign-in, so
  // future Google/Apple sign-ins match directly. Keeps accounts
  // invite-only (nothing is created) while giving every Apple ID a
  // working, designed path — including App Review's, via the demo creds.
  if (pendingSsoLink && showLinkGuidance) {
    const providerLabel =
      pendingSsoLink.provider === 'apple' ? 'Apple ID' : 'Google account';
    // Informational only — deliberately NO input fields. App Review
    // (Guideline 4) rejects a password form presented as the next step of
    // Sign in with Apple. The button returns to the standard sign-in form;
    // the pending link completes silently after that normal sign-in.
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-secondary/5 px-4 py-12">
        <Card className="w-full max-w-md shadow-xl" variant="elevated">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-text mb-2">
              No account linked yet
            </h1>
            <p className="text-text-secondary text-sm">
              That {providerLabel} isn't connected to a Dispatch account.
              Dispatch accounts are created by your ISP administrator — there
              is nothing to sign up for here.
            </p>
            <p className="text-text-secondary text-sm mt-3">
              If your ISP has given you a Dispatch account, continue with
              your work email. Your {providerLabel} will be connected
              automatically so you can use it from then on.
            </p>
          </div>
          <Button
            type="button"
            className="w-full"
            onClick={() => setShowLinkGuidance(false)}
          >
            Continue with work email
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="w-full mt-3"
            onClick={() => {
              setPendingSsoLink(null);
              setShowLinkGuidance(false);
            }}
          >
            Cancel
          </Button>
          <p className="text-xs text-text-secondary text-center mt-4">
            Don't have a Dispatch account? Ask your ISP administrator to
            invite you.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-secondary/5 px-4 py-12">
      <Card className="w-full max-w-md shadow-xl" variant="elevated">
        <div className="text-center mb-8">
          {safeLogoUrl && (
            <div className="mb-6">
              <img
                src={safeLogoUrl}
                alt={tenantConfig?.companyName || 'Logo'}
                className="h-16 w-auto mx-auto"
                loading="lazy"
                referrerPolicy="no-referrer"
              />
            </div>
          )}
          {/* Native cold start has no tenant subdomain yet, so tenantConfig
              is null — brand the screen as Dispatch instead of rendering an
              empty heading. */}
          {isNative && !tenantConfig?.companyName && !safeLogoUrl && (
            <img
              src="/icons/icon-96.webp"
              alt=""
              aria-hidden="true"
              className="h-16 w-16 rounded-2xl mx-auto mb-4"
            />
          )}
          <h1 className="text-3xl font-bold text-text mb-2">
            {tenantConfig?.companyName || (isNative ? 'Dispatch' : '')}
          </h1>
          <p className="text-text-secondary text-base">Sign in to your account</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input
            label="Email"
            type="email"
            placeholder="you@example.com"
            autoComplete="email"
            error={errors.email?.message}
            {...register('email')}
          />

          <Input
            label="Password"
            type="password"
            placeholder="Enter your password"
            autoComplete="current-password"
            error={errors.password?.message}
            {...register('password')}
          />

          <Button
            type="submit"
            className="w-full"
            loading={loading}
            disabled={isBlocked}
          >
            {isBlocked ? `Wait ${remainingTime}s` : 'Sign In'}
          </Button>
        </form>

        {/* Google Sign-in */}
        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-surface text-text-secondary">Or continue with</span>
            </div>
          </div>

          {googleEnabled && (
            <Button
              type="button"
              variant="outline"
              className="w-full mt-4 flex items-center justify-center gap-2"
              onClick={handleGoogleLogin}
              loading={googleLoading}
              disabled={loading || isBlocked || appleLoading}
            >
              <GoogleIcon className="w-5 h-5" />
              Continue with Google
            </Button>
          )}

          {appleEnabled && (
            // Apple's brand guidelines require this exact label, black bg,
            // white text + logo, and ~44px height on most platforms. The
            // glyph is inline SVG so we don't pull in an extra dependency.
            <Button
              type="button"
              className="w-full mt-3 flex items-center justify-center gap-2 bg-black hover:bg-black/90 text-white border-black"
              onClick={handleAppleLogin}
              loading={appleLoading}
              disabled={loading || isBlocked || googleLoading}
            >
              <svg
                aria-hidden="true"
                className="w-4 h-4 -mt-0.5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M14.94 10.6c-.02-2.04 1.67-3.02 1.74-3.07-.95-1.39-2.43-1.58-2.96-1.6-1.26-.13-2.46.74-3.1.74-.64 0-1.63-.72-2.68-.7-1.38.02-2.65.8-3.36 2.04-1.43 2.48-.37 6.16 1.03 8.18.68.98 1.5 2.09 2.55 2.05 1.02-.04 1.41-.66 2.64-.66 1.23 0 1.58.66 2.66.64 1.1-.02 1.8-1 2.48-1.99.78-1.14 1.1-2.25 1.12-2.31-.02-.01-2.15-.83-2.16-3.32zM13.06 5.07c.56-.69.94-1.64.83-2.59-.81.03-1.79.54-2.36 1.22-.51.6-.96 1.57-.84 2.5.9.07 1.82-.46 2.37-1.13z"/>
              </svg>
              Continue with Apple
            </Button>
          )}
        </div>

        <div className="mt-6 text-center space-y-4">
          <Link to="/forgot-password" className="text-sm text-primary hover:text-primary-dark font-medium transition-colors">
            Forgot your password?
          </Link>
          {!isNative && (
            <div className="pt-4 border-t border-border">
              <p className="text-sm text-text-secondary">
                Don't have an account?{' '}
                <Link to="/register" className="text-primary hover:text-primary-dark font-medium transition-colors">
                  Sign up now
                </Link>
              </p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
